import { Component, OnInit, isDevMode, Input } from '@angular/core';
import { TransferHttpService, CacheItem } from 'src/app/services/transfer-http/transfer-http.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { tap, delay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth/auth.service';
import { NgUsers } from 'src/models';
import { AimsCommonTablePermissions, AimsCommonConfirmDialogComponent, AimsCommonButtonComponent, AimsCommonTableHeaderType, AimsCommonTableHeader, AimsCommonPopupEditorComponent } from '@aims/common-components';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent, SpinnerText } from 'src/app/services/message/message.enum';
import { MessageService } from 'src/app/services/message/message.service';
import { GetTextPipe } from 'src/app/pipes';
import { ManageUsersPopupComponent } from './manage-users-popup/manage-users-popup.component';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.css'],
  providers: [GetTextPipe]
})
export class ManageUsersComponent implements OnInit {

  constructor(private transferHttp: TransferHttpService,
    public permissionsService: PermissionsService,
    public authService: AuthService,
    private dialog: MatDialog,
    private messageService: MessageService,
    private getTextPipe: GetTextPipe
    ) {  }
  //Variables
  canCreate = this.permissionsService.canCreate(this.permissionsService.ComponentID.AIMSUsers);
  canUpdate = this.permissionsService.canUpdate(this.permissionsService.ComponentID.AIMSUsers);
  canDelete = this.permissionsService.canDelete(this.permissionsService.ComponentID.AIMSUsers);

  permissions: AimsCommonTablePermissions = { create: false, update: this.canUpdate, delete: this.canDelete, export: false }

  //Dropdowns
  users: Observable<any> = this.getUsers();
  tenants;
  employees = this.transferHttp.getCache(CacheItem.allEmployees);
  user = this.transferHttp.getCache(CacheItem.user);

  //Spinner
  showSpinner: boolean = false;
  spinnerText: string = "Loading...";

  //Form
  userToEdit: User;
  userIsNew: boolean;
  defaultTableState = {filter:{'active':true}}
  imgSrc: string = environment.assetsPath;
  //Columns
  manageUsersColumns:AimsCommonTableHeader[] = [
    {
      key: 'deactivate',
      label: (element) => ( element.active ? 'Deactivate' : 'Activate' ),
      type: 'button',
      filter: false,
      events: {
        click: (element) => this.handleRowDeactivated(element)
      }
    },
    {
      key: 'edit',
      label: 'Edit/Password Reset',
      type: 'button',
      filter: false,
      events: {
        click: (element) => this.handleRowEdit(element)
      }
    },
    { key: 'userName', label: 'User Name', type: AimsCommonTableHeaderType.STRING },
    { key: 'employeeId', label: 'Employee Record', type: AimsCommonTableHeaderType.DYNAMIC,
      events: { filterAndSort: (element) => this.getTextPipe.transform(element.employeeId, 'employeeId', 'fullName', this.employees) }
    },
    { key: 'lastSuccessfulLogin', label: 'Last Successful Login', type: AimsCommonTableHeaderType.DATE, format: 'MM/dd/yyyy HH:mm', filterType: 'date' },
    { key: 'lastPasswordReset', label: 'Last Password Reset', type: AimsCommonTableHeaderType.DATE, format: 'MM/dd/yyyy HH:mm', filterType: 'date' },
    { key: 'passwordExpirationDate', label: 'Password Expiration', type: AimsCommonTableHeaderType.DATE, format: 'MM/dd/yyyy', filterType: 'date' },
    { key: 'active', label: 'Active', type: AimsCommonTableHeaderType.DYNAMIC, filterType: 'boolean' }
  ];
  manageUsersDataSource: User[];
  //Event Handlers
  openPopup(userToEdit:User, isNew:boolean){

    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
      data: {
        header: 'User',
        isNew: isNew,
        content: ManageUsersPopupComponent,
        contentData: userToEdit,
      },
      disableClose: true
    })
    dialogRef.afterClosed().subscribe(dialogResult => {
      if(dialogResult){
        this.userToEdit = dialogResult;
        //modify the object, so it's ready to save
        if(isNew){
          //creates a new user
          this.userToEdit.userId = 0;
        }
        if(this.userToEdit.generatePwd){
          this.userToEdit.password = null;
          this.userToEdit.confirmPassword = null;
        }
        this.handlePopupSave(this.userToEdit);
      }
    });
  }
  handleRowEdit(element){
    const user = element as NgUsers;
    this.userToEdit = new User(user.tenantId, user);
    this.openPopup(this.userToEdit, false);
  }
  handleRowAdd(event:any){
    this.manageUsersDataSource = [new User(this.user.tenantId), ...this.manageUsersDataSource];
    this.userToEdit = new User(this.user.tenantId);
    this.openPopup(this.userToEdit, true)
  }
  handleRowDeactivated(element){
    const user = element as User;
    //Confirm if deactivating user, otherwise just active the user
    if(element.active) {
      var message = this.messageService.getDialogMessage(DialogComponent.DeactivateManageUsers);
      let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent, {
        data: message
      });
      dialogRef.afterClosed().subscribe(dialogResult => {
        if(dialogResult){
          this.changeUserActivation(user);
        }
        dialogRef = null;
      })
    } else {
      this.changeUserActivation(user);
    }

  }
  handlePopupCancel() {
    this.userToEdit = undefined;
    //reset the dataSource, otherwise there's an empty row
    this.manageUsersDataSource = this.manageUsersDataSource.filter(u=>u.userId);
  }
  handlePopupSave(user: User) {
    //Will still match undefined if nothing entered in UI
    if (user.password == user.confirmPassword) {
      this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "User");
      this.showSpinner = true;

      this.permissionsService.saveUser(user.userId, user.userName, user.email, user.password, user.employeeId, user.generatePwd)
      .subscribe(r => {
        this.users = this.getUsers()
        this.showSpinner = false;
      },
        error => this.showSpinner = false
      );
    }
  }
  changeUserActivation(user:User):void{
    this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "User");
    this.showSpinner = true;

    this.permissionsService.changeUserActivation(user.userId)
    .subscribe(a => {
      this.users = this.getUsers();
      this.showSpinner = false;
    },
      error => this.showSpinner = false
    );
  }
  getUsers(){
    return this.permissionsService.getUsers().pipe(
      delay(0),
      tap(u => {
        this.manageUsersDataSource = u;
      })
    );
  }




  disablePwd: boolean = false;
  autoGenChange(){
    if (this.userToEdit.generatePwd){
      this.userToEdit.password = null;
      this.userToEdit.confirmPassword = null;
      this.disablePwd = true;
    }
    else{
      this.disablePwd = false;
    }
  }

  ngOnInit() {
    this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Load);
    this.showSpinner = true;

    this.authService.getTenants().subscribe(tenants => {
      this.tenants = tenants
      this.showSpinner = false;
    },
      error => this.showSpinner = false
    );
  }

}

export class User {
  constructor(tenantId: number, user: NgUsers = null){
    this.tenantId = tenantId;
    if (user !== null){
      this.userId = user.userId;
      this.userName = user.userName;
      this.employeeId = user.employeeId;
      this.email = user.email;
    }
    this.password = "";
    this.confirmPassword = "";
    this.generatePwd = false;
  }

  userId: number = 0;
  userName: string;
  password: string;
  confirmPassword: string;
  tenantId: number;
  employeeId: number = 0;
  email: string;
  generatePwd: boolean = false;
}
